import React, { Fragment, useCallback } from "react";

import {
  PopupMenu,
  Kit,
  Space,
  Tooltip,
  Button,
  SpacePermission,
  KitVersion,
  KitPermission,
  buildURL,
  copy,
} from "@thenounproject/lingo-core";

import useShowModal, { ModalTypes } from "@redux/actions/useModals";
import useUpsell, { AccessStatus } from "@hooks/useUpsell";
import useNotifications from "@actions/useNotifications";
import useDuplicateKit from "@redux/actions/kits/useDuplicateKit";
import { useSelectedItemsContext } from "@contexts/SelectedItemsProvider";
import { useLocation } from "react-router-dom";
import useNavPoint from "@hooks/useNavPoint";
import useConfigureMode from "../../components/hooks/useConfigureMode";
import useKitUpsell from "@hooks/useKitUpsell";

type Props = {
  kit: Kit;
  draft: KitVersion;
  space: Space;
  activeVersion?: number;
  portalId?: string;
  portalItemId?: string;
};

export function KitContextMenuItems({
  kit,
  draft,
  space,
  // Some options may be be ommitted when viewing a release version
  activeVersion = 0,
}: Props) {
  const [duplicateKit] = useDuplicateKit();
  const location = useLocation();
  const navPoint = useNavPoint();
  const { clearSelection } = useSelectedItemsContext();

  const permissions = kit.access?.permissions || [],
    spacePermissions = space?.access?.permissions || [],
    canEdit = permissions.includes(KitPermission.edit),
    canEditContent = permissions.includes(KitPermission.editContent),
    canDelete = permissions.includes(KitPermission.delete),
    canRemove = permissions.includes(KitPermission.manage) && kit.purchase;

  const insightsAccess = useUpsell(SpacePermission.viewAdvancedAnalytics);
  const themeAccess = useKitUpsell(KitPermission.manageCustomTheme, kit);

  const { showModal } = useShowModal();
  const { showNotification } = useNotifications();

  const duplicateSelected = useCallback(async () => {
    const { error } = await duplicateKit({
      kitId: kit.kitId,
      spaceId: space?.id,
    });

    if (error) {
      showNotification({
        message: `There was a problem duplicating your kit ${error.message}`,
        level: "error",
      });
    } else {
      showNotification({
        message: "Your kit is being duplicated. It may take up to a few minutes.",
      });
    }
  }, [duplicateKit, kit.kitId, showNotification, space?.id]);

  const openShareModal = useCallback(() => {
    if (navPoint.portalId) {
      showModal(ModalTypes.SHARE_PORTAL, { portalId: navPoint.portalId, kitId: kit.kitId });
    } else {
      showModal(ModalTypes.SHARE_KIT, { kitId: kit.kitId });
    }
  }, [kit.kitId, showModal, navPoint.portalId]);

  function renderShareItem() {
    return permissions.includes(KitPermission.changeOwnStatus) ? (
      <PopupMenu.Item key="kit-menu-share" title="Share kit" onClick={openShareModal} />
    ) : (
      <PopupMenu.Item
        key="kit-menu-copy-share-link"
        title="Copy share link"
        onClick={() =>
          copy(buildURL(`/k/${kit.shortId}`, { space, portal: navPoint.portal, isPublic: true }))
        }
      />
    );
  }

  function renderSharedKitDetailsItem() {
    if (!kit.purchase) return null;
    if (!showModal) return null;
    return (
      <PopupMenu.Item
        key="kit-menu-shared-kit-details"
        title="License & details"
        onClick={() => showModal(ModalTypes.PUBLISHED_KIT_META, { kit })}
      />
    );
  }

  function renderViewInsightsItem() {
    if (insightsAccess.accessStatus === AccessStatus.insufficientRole) return null;

    const needsUpgrade = insightsAccess.accessStatus === AccessStatus.insufficientPlan;
    return (
      <React.Fragment key="kit-menu-view-insights">
        <PopupMenu.Item
          title="View insights"
          disabled={needsUpgrade}
          onClick={() =>
            showModal(ModalTypes.INSIGHTS_KIT, { kitId: kit.kitId, kitName: kit.name, space })
          }
          data-tooltip-source="insights-kit-menu-item"
        />
        {needsUpgrade && (
          <Tooltip source="insights-kit-menu-item" direction={Tooltip.Direction.Right}>
            Insights are not available on your current plan.{" "}
            <Button
              buttonStyle="tertiary"
              themeOverrides={{ primaryColor: "white" }}
              fontStyle="ui.smallBold"
              text="View plans."
              onClick={insightsAccess.openUpgradeModal}
            />
          </Tooltip>
        )}
      </React.Fragment>
    );
  }

  // Section 2
  function renderEditItem() {
    if (!canEdit || activeVersion > 0) return null;
    if (!showModal) return null;
    return (
      <PopupMenu.Item
        key="kit-menu-edit-kit"
        title="Edit kit"
        onClick={() => showModal(ModalTypes.EDIT_KIT, { kit: kit })}
      />
    );
  }
  const { enterConfigureMode: _enterConfigureMode } = useConfigureMode();
  const enterConfigureMode = useCallback(() => {
    clearSelection({ type: "kitGallery" });
    _enterConfigureMode();
  }, [_enterConfigureMode, clearSelection]);

  function renderKitThemeItem() {
    if (
      activeVersion > 0 ||
      themeAccess.accessStatus === AccessStatus.insufficientRole ||
      !navPoint.kit
    )
      return null;

    const needsUpgrade = themeAccess.accessStatus === AccessStatus.insufficientPlan;
    return (
      <>
        <PopupMenu.Item
          key="kit-menu-configure-kit"
          data-tooltip-source="kit-menu-configure-kit"
          disabled={needsUpgrade}
          title="Configure kit styling"
          onClick={enterConfigureMode}
        />
        {needsUpgrade && (
          <Tooltip source="kit-menu-configure-kit" direction={Tooltip.Direction.Left}>
            Kit styling is not available on your current plan.{" "}
            <Button
              buttonStyle="tertiary"
              themeOverrides={{ primaryColor: "white", primaryColorDark: "white" }}
              fontStyle="ui.smallBold"
              text="View plans."
              onClick={themeAccess.openUpgradeModal}
            />
          </Tooltip>
        )}
      </>
    );
  }
  function renderDuplicateKitItem() {
    if (!duplicateKit) return null;
    if (!canEdit || !spacePermissions.includes("create_kit") || activeVersion > 0) {
      return null;
    }
    return (
      <PopupMenu.Item
        key="kit-menu-duplicate-kit"
        title="Duplicate kit"
        onClick={duplicateSelected}
      />
    );
  }

  function renderCollectionsItem() {
    if (space.features.includes("portals")) return null;
    if (!canEdit || !spacePermissions.includes(SpacePermission.editKitCollection)) {
      return null;
    }
    return (
      <PopupMenu.Item
        key="kit-menu-collections"
        title="Add to collections"
        onClick={() => showModal(ModalTypes.MANAGE_KIT_COLLECTIONS, { kit })}
      />
    );
  }

  const previous = location?.search.includes("?previous=")
    ? location.search
    : `?previous=${location?.pathname}`;

  function renderRecentlyDeletedItem() {
    if (!draft) return null;
    if (!canEditContent || activeVersion > 0) return null;
    return (
      <PopupMenu.Item
        key="kit-menu-recently-deleted"
        title="Recently removed"
        disabled={draft.counts.trash === 0}
        link={buildURL(`/k/${kit.urlId}/deleted/${previous}`, { space, portal: navPoint.portal })}
      />
    );
  }

  function renderRecoveredAssetsItem() {
    if (!draft) return null;
    if (!canEditContent || activeVersion > 0 || draft.counts.recovery === 0) return null;
    return (
      <PopupMenu.Item
        key="kit-menu-recovered-assets"
        title="Recovered assets"
        link={buildURL(`/k/${kit.urlId}/recovered/${previous}`, {
          space,
          portal: navPoint.portal,
        })}
      />
    );
  }

  // Section 3
  function renderDeleteItem() {
    if (!(canDelete || canRemove) || activeVersion > 0) {
      return null;
    }

    return (
      <PopupMenu.Item
        key="kit-menu-delete-kit"
        title={kit.purchase ? "Remove kit" : "Delete kit"}
        onClick={() => showModal(ModalTypes.DELETE_KIT, { kit: kit })}
      />
    );
  }

  const shareOptions = [
    renderShareItem(),
    renderSharedKitDetailsItem(),
    renderViewInsightsItem(),
  ].filter(Boolean);

  const editOptions = [
    renderEditItem(),
    renderKitThemeItem(),
    renderDuplicateKitItem(),
    renderCollectionsItem(),
    renderRecentlyDeletedItem(),
    renderRecoveredAssetsItem(),
  ].filter(Boolean);
  const deleteOptions = [renderDeleteItem()].filter(Boolean);

  const sections = [shareOptions, editOptions, deleteOptions].filter(s => s.length);

  if (!sections.length) return null;

  return (
    <Fragment>
      {sections.map((s, idx) => (
        <PopupMenu.Section key={`kit-menu-section-${idx}`}>{s}</PopupMenu.Section>
      ))}
    </Fragment>
  );
}
